import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Check } from "lucide-react";

import { useSeo } from "@/hooks/useSeo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { Icon } from "@/components/Icon";
import { IMAGES, VENDOR_BENEFITS } from "@/data/content";
import { submitVendor } from "@/lib/api";

const FIELD =
  "w-full rounded-xl border border-hairline bg-white px-4 py-3 text-inktext placeholder:text-steel/60 focus:outline-none focus:border-accent2 focus:ring-2 focus:ring-accent2/20 transition-colors";
const LABEL = "block text-sm font-semibold text-navy mb-2";

export default function VendorPartners() {
  useSeo({
    title: "Vendor Partners",
    description:
      "Partner with Meridian Vanguard Solutions. We seek manufacturers, distributors, wholesalers, and service providers committed to quality, reliability, and ethical business.",
    path: "/vendor-partners",
    breadcrumb: [{ name: "Home", path: "/" }, { name: "Vendor Partners", path: "/vendor-partners" }],
  });

  const [form, setForm] = useState({ company: "", website: "", products: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.company.trim()) e.company = "Company is required";
    if (!form.products.trim()) e.products = "Please list your products/services";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await submitVendor(form);
      toast.success("Application received. Our team will review and follow up.");
      setForm({ company: "", website: "", products: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Partner With Us"
        titleLines={["Vendor Partnerships"]}
        subtitle="Meridian Vanguard Solutions is continuously expanding its network of trusted manufacturers, distributors, wholesalers, and service providers."
        image={IMAGES.cargoShip}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Vendor Partners", path: "/vendor-partners" }]}
      />

      {/* Intro */}
      <section className="bg-white py-24 md:py-32">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            eyebrow="Why Partner"
            title="Grow with a dependable government contracting partner"
            description="We seek organizations committed to quality products, dependable service, competitive pricing, and ethical business practices. Together, we deliver efficient procurement solutions for government clients."
          />
          <StaggerGroup stagger={0.06} className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VENDOR_BENEFITS.map((b) => (
              <StaggerItem key={b.title}>
                <div className="group h-full rounded-xl border border-hairline bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-navy/20 hover:shadow-[0_16px_36px_rgba(10,35,66,0.09)]">
                  <div className="h-14 w-14 rounded-xl bg-lightgray flex items-center justify-center transition-colors group-hover:bg-navy">
                    <Icon name={b.icon} className="h-7 w-7 text-navy transition-colors group-hover:text-white" />
                  </div>
                  <h3 className="mt-6 font-heading text-xl font-semibold text-navy">{b.title}</h3>
                  <p className="mt-3 text-steel leading-relaxed text-[15px]">{b.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Become a vendor form */}
      <section className="bg-lightgray py-24 md:py-32">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5">
            <span className="text-sm font-semibold uppercase tracking-[0.22em] text-accent2">Become a Vendor</span>
            <h2 className="mt-4 font-heading text-4xl font-bold text-navy tracking-tight leading-tight">Join our supplier network</h2>
            <p className="mt-6 text-steel leading-relaxed text-lg">
              Tell us about your organization and the products or services you offer. Qualified
              partners gain visibility into government opportunities and a reliable purchasing
              process.
            </p>
            <ul className="mt-8 space-y-3">
              {["Long-term partnerships", "Government opportunities", "Professional communication", "Reliable purchasing process", "Strategic growth"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-navy font-medium">
                  <span className="h-6 w-6 rounded-full bg-navy flex items-center justify-center shrink-0"><Check size={14} className="text-white" /></span>
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form data-testid="vendor-form" onSubmit={onSubmit} className="rounded-xl bg-white border border-hairline p-8 md:p-10 space-y-6" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="v-company" className={LABEL}>Company *</label>
                  <input id="v-company" data-testid="vendor-company" className={FIELD} value={form.company} onChange={update("company")} placeholder="Company name" />
                  {errors.company && <p className="mt-1.5 text-sm text-red-700">{errors.company}</p>}
                </div>
                <div>
                  <label htmlFor="v-website" className={LABEL}>Website</label>
                  <input id="v-website" data-testid="vendor-website" className={FIELD} value={form.website} onChange={update("website")} placeholder="https://" />
                </div>
              </div>
              <div>
                <label htmlFor="v-products" className={LABEL}>Products / Services *</label>
                <input id="v-products" data-testid="vendor-products" className={FIELD} value={form.products} onChange={update("products")} placeholder="What do you supply?" />
                {errors.products && <p className="mt-1.5 text-sm text-red-700">{errors.products}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="v-email" className={LABEL}>Email *</label>
                  <input id="v-email" type="email" data-testid="vendor-email" className={FIELD} value={form.email} onChange={update("email")} placeholder="name@company.com" />
                  {errors.email && <p className="mt-1.5 text-sm text-red-700">{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor="v-phone" className={LABEL}>Phone</label>
                  <input id="v-phone" data-testid="vendor-phone" className={FIELD} value={form.phone} onChange={update("phone")} placeholder="(000) 000-0000" />
                </div>
              </div>
              <div>
                <label htmlFor="v-message" className={LABEL}>Message</label>
                <textarea id="v-message" rows={4} data-testid="vendor-message" className={FIELD} value={form.message} onChange={update("message")} placeholder="Tell us more about your organization" />
              </div>
              <button
                type="submit"
                data-testid="vendor-submit"
                disabled={loading}
                className="group inline-flex items-center gap-3 rounded-xl bg-navy text-white px-8 py-4 font-semibold hover:bg-accent2 transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {loading ? "Submitting..." : "Become a Vendor"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
